import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Card from './Card';
import { Suit, Rank, Card as CardT } from '../../types/Card';
import { ClientGameState, ClientPlayer } from '../../types/Socket';
import { canPlayCard } from '../logic/deckUtils';
import { socket } from '../../socket';
import { Sounds, setSoundEnabled, isSoundEnabled, setVolume, getVolume } from '../utils/sounds';
import { Haptics } from '../utils/haptics';
import { recordLocalGameResult } from '../../utils/api';
import { getPlayerId } from '../../utils/playerIdentity';
import RulesModal from './RulesModal';
import Profile from './Profile';
import ConfirmDialog from './ConfirmDialog';
import ControlsIntro from './ControlsIntro';
import './GameTable.css';

/* ===== Helpers ===== */

const SUIT_SYMBOLS: Record<string, string> = {
  spades: '\u2660',
  hearts: '\u2665',
  diamonds: '\u2666',
  clubs: '\u2663',
};

const isRedSuit = (suit: string) => suit === 'hearts' || suit === 'diamonds';

// Alternating black-red-black-red so adjacent groups are visually distinct
const SUIT_ORDER: Record<string, number> = { spades: 0, diamonds: 1, clubs: 2, hearts: 3 };
const RANK_ORDER: Record<string, number> = {
  '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8,
  '9': 9, '10': 10, J: 11, Q: 12, K: 13, A: 14,
};

type SortMode = 'suit' | 'rank' | 'none';

const sortCards = (cards: CardT[], mode: SortMode): CardT[] => {
  if (mode === 'none') return cards;
  return [...cards].sort((a, b) => {
    if (mode === 'suit') {
      const suitDiff = (SUIT_ORDER[a.suit] ?? 0) - (SUIT_ORDER[b.suit] ?? 0);
      if (suitDiff !== 0) return suitDiff;
      return (RANK_ORDER[a.rank] ?? 0) - (RANK_ORDER[b.rank] ?? 0);
    }
    // mode === 'rank'
    const rankDiff = (RANK_ORDER[a.rank] ?? 0) - (RANK_ORDER[b.rank] ?? 0);
    if (rankDiff !== 0) return rankDiff;
    return (SUIT_ORDER[a.suit] ?? 0) - (SUIT_ORDER[b.suit] ?? 0);
  });
};

const SORT_LABELS: Record<SortMode, string> = { suit: 'Suit', rank: 'Rank', none: 'Off' };
const SORT_CYCLE: SortMode[] = ['suit', 'rank', 'none'];

const PLAYER_AVATARS = [
  { emoji: '\uD83E\uDD20', bg: '#b45309' },
  { emoji: '\uD83E\uDD78', bg: '#7c3aed' },
  { emoji: '\uD83D\uDE0E', bg: '#0369a1' },
  { emoji: '\uD83E\uDDD9', bg: '#4338ca' },
  { emoji: '\uD83E\uDD16', bg: '#0f766e' },
  { emoji: '\uD83D\uDC7B', bg: '#a21caf' },
  { emoji: '\uD83E\uDD8A', bg: '#c2410c' },
  { emoji: '\uD83D\uDC09', bg: '#15803d' },
];

type ScreenOrientationApi = ScreenOrientation & {
  lock?: (orientation: string) => Promise<void>;
  unlock?: () => void;
};

const getPlayerSeatPosition = (index: number, total: number, mobile: boolean, myIndex: number, landscape: boolean = false) => {
  const visualIdx = (index - myIndex + total) % total;
  const angle = Math.PI / 2 + (visualIdx / total) * 2 * Math.PI;
  let rx: number, ry: number;
  if (landscape) {
    rx = 38;
    ry = 34;
  } else if (mobile) {
    rx = 34;
    ry = 30;
  } else {
    rx = 42;
    ry = 40;
  }
  return {
    left: `${50 + rx * Math.cos(angle)}%`,
    top: `${50 + ry * Math.sin(angle)}%`,
    transform: 'translate(-50%, -50%)',
  } as React.CSSProperties;
};

const getCenterCardPosition = (
  playerIdx: number,
  cardIdx: number,
  totalPlayers: number,
  myIndex: number
) => {
  const visualIdx = (playerIdx - myIndex + totalPlayers) % totalPlayers;
  const angle = Math.PI / 2 + (visualIdx / totalPlayers) * 2 * Math.PI;
  const radius = 18 + cardIdx * 3;
  const x = 50 + radius * Math.cos(angle);
  const y = 50 + radius * Math.sin(angle);
  return {
    position: 'absolute' as const,
    top: `${y}%`,
    left: `${x}%`,
    transform: 'translate(-50%, -50%)',
  };
};

/* ===== Component ===== */

interface GameTableProps {
  roomCode: string;
  playerName: string;
  onLeave: () => void;
}

const GameTable: React.FC<GameTableProps> = ({ roomCode, playerName, onLeave }) => {
  const [gameState, setGameState] = useState<ClientGameState | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);
  const [showBorrowMenu, setShowBorrowMenu] = useState<number | null>(null);
  const [notifications, setNotifications] = useState<Array<{ id: number; type: string; message: string }>>([]);
  const [timerSeconds, setTimerSeconds] = useState<number | null>(null);
  const [showCut, setShowCut] = useState(false);
  const [showYourTurn, setShowYourTurn] = useState(false);
  const [cutFly, setCutFly] = useState<{
    targetIdx: number;
    cards: Array<{ id: string; suit: string; rank: string; playerIndex: number }>;
  } | null>(null);
  const [flyStarted, setFlyStarted] = useState(false);
  const [safeAnimIdx, setSafeAnimIdx] = useState<number | null>(null);
  const [soundOn, setSoundOn] = useState(isSoundEnabled());
  const [volume, setVolumeState] = useState(getVolume());
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(!!document.fullscreenElement);
  const soundLongPressRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const soundTapFiredRef = useRef(false);
  const [showRules, setShowRules] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [sortMode, setSortMode] = useState<SortMode>('suit');
  const [confirmMode, setConfirmMode] = useState(() => localStorage.getItem('ace_confirm_mode') === 'true');
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [showControlsIntro, setShowControlsIntro] = useState(false);
  const hasShownIntroRef = useRef(false);
  const hasShownPenaltyRef = useRef(false);
  const [showRotateHint, setShowRotateHint] = useState(false);
  const [hasAcknowledgedLandscapeGate, setHasAcknowledgedLandscapeGate] = useState(false);
  const [isEnteringLandscapeMode, setIsEnteringLandscapeMode] = useState(false);
  const [orientationLockFailed, setOrientationLockFailed] = useState(false);
  const [isResyncing, setIsResyncing] = useState(false);
  const hasShownRotateRef = useRef(false);
  const hasShownResignNudgeRef = useRef(false);
  const [dealing, setDealing] = useState(true);
  const [dealtCount, setDealtCount] = useState(0);
  const hasConnectedOnceRef = useRef(false);

  const tableRef = useRef<HTMLDivElement>(null);
  const hasDealtRef = useRef(false);
  const dealSeqRef = useRef<number[]>([]);
  const dealTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutsRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
  const prevRef = useRef({
    roundStopped: false,
    isMyTurn: false,
    gameOver: false,
    centerPileLen: 0,
    dealing: true,
    safeSet: new Set<number>(),
  });

  useEffect(() => {
    const check = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      setIsMobile(w <= 768 || (w <= 1024 && h <= 500));
      setIsLandscape(w > h && h <= 500);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const scheduleTimeout = useCallback((fn: () => void, delay: number) => {
    const timer = setTimeout(() => {
      timeoutsRef.current.delete(timer);
      fn();
    }, delay);
    timeoutsRef.current.add(timer);
    return timer;
  }, []);

  // Show a one-time "rotate for best experience" hint on mobile portrait
  useEffect(() => {
    if (
      isMobile &&
      !isLandscape &&
      !hasShownRotateRef.current &&
      gameState &&
      !gameState.gameOver &&
      !dealing &&
      !localStorage.getItem('ace_rotate_hint_seen')
    ) {
      hasShownRotateRef.current = true;
      localStorage.setItem('ace_rotate_hint_seen', '1');
      setShowRotateHint(true);
      const timer = scheduleTimeout(() => setShowRotateHint(false), 4000);
      const timeoutSet = timeoutsRef.current;
      return () => {
        clearTimeout(timer);
        timeoutSet.delete(timer);
      };
    }
  }, [isMobile, isLandscape, gameState, dealing, scheduleTimeout]);

  // Warn on tab close / refresh (keep minimal browser dialog for actual tab close only)
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!gameState?.gameOver) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [gameState?.gameOver]);

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);



  // Socket listeners
  const notificationTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    const onGameUpdate = (data: ClientGameState) => {
      setGameState(data);
      setShowBorrowMenu(null);
      setSelectedCardId(null);
      setIsResyncing(false);
    };

    const onNotification = (data: { type: string; message: string }) => {
      const id = Date.now();
      setNotifications((prev) => [...prev.slice(-4), { id, ...data }]);
      const timer = scheduleTimeout(() => {
        setNotifications((prev) => prev.filter((n) => n.id !== id));
      }, 4000);
      notificationTimersRef.current.push(timer);
    };

    const onError = ({ message }: { message: string }) => {
      onNotification({ type: 'error', message });
      setIsResyncing(false);
      
      // If the server restarted and the room is gone, automatically send the player back to lobby
      if (message.includes('Room not found') || message.includes('could not be reconnected')) {
        scheduleTimeout(() => {
          onLeave();
        }, 2500);
      }
    };

    const onReconnect = () => {
      if (!hasConnectedOnceRef.current) {
        hasConnectedOnceRef.current = true;
        socket.emit('request_game_state');
        return;
      }
      setIsResyncing(true);
      socket.emit('join_room', {
        roomCode,
        playerName,
        playerId: getPlayerId(),
      });
    };
    const onDisconnect = () => {
      setIsResyncing(true);
      setShowBorrowMenu(null);
      setSelectedCardId(null);
    };
    const onRejoinGame = () => {
      socket.emit('request_game_state');
    };

    socket.on('game_update', onGameUpdate);
    socket.on('notification', onNotification);
    socket.on('error_msg', onError);
    socket.on('connect', onReconnect);
    socket.on('disconnect', onDisconnect);
    socket.on('rejoin_game', onRejoinGame);

    if (socket.connected) {
      hasConnectedOnceRef.current = true;
      socket.emit('request_game_state');
    }

    return () => {
      socket.off('game_update', onGameUpdate);
      socket.off('notification', onNotification);
      socket.off('error_msg', onError);
      socket.off('connect', onReconnect);
      socket.off('disconnect', onDisconnect);
      socket.off('rejoin_game', onRejoinGame);
      // eslint-disable-next-line react-hooks/exhaustive-deps
      notificationTimersRef.current.forEach(clearTimeout);
      notificationTimersRef.current = [];
    };
  }, [playerName, roomCode, scheduleTimeout, onLeave]);

  const triggerDealAnimation = useCallback((gs: ClientGameState) => {
    hasDealtRef.current = true;
    const dealKey = `ace_dealt_${roomCode}`;
    sessionStorage.setItem(dealKey, 'true');

    setDealing(true);
    setDealtCount(0);

    // Build round-robin deal sequence matching real card distribution
    const seq: number[] = [];
    const counts = gs.players.map((p) => p.cardCount);
    const maxCards = Math.max(...counts);
    for (let round = 0; round < maxCards; round++) {
      for (let pIdx = 0; pIdx < gs.players.length; pIdx++) {
        if (round < counts[pIdx]) seq.push(pIdx);
      }
    }
    dealSeqRef.current = seq;

    Sounds.shuffle();

    const CARD_INTERVAL = 70;
    let count = 0;
    if (dealTimerRef.current) clearInterval(dealTimerRef.current);
    dealTimerRef.current = setInterval(() => {
      count++;
      setDealtCount(count);
      Sounds.dealCard();
      if (count >= seq.length) {
        if (dealTimerRef.current) clearInterval(dealTimerRef.current);
        dealTimerRef.current = null;
        scheduleTimeout(() => setDealing(false), 800);
      }
    }, CARD_INTERVAL);
  }, [roomCode, scheduleTimeout]);

  // Dealing animation on game start
  useEffect(() => {
    if (!gameState || hasDealtRef.current) return;
    
    const dealKey = `ace_dealt_${roomCode}`;
    const alreadyDealtInSession = sessionStorage.getItem(dealKey) === 'true';
    const isRejoin = alreadyDealtInSession
      || gameState.centerPile.length > 0
      || gameState.roundStopped
      || gameState.leadSuit !== null
      || gameState.gameOver
      || gameState.players.some((p) => p.isSafe);

    if (isRejoin) {
      hasDealtRef.current = true;
      sessionStorage.setItem(dealKey, 'true');
      setDealing(false);
      return;
    }

    triggerDealAnimation(gameState);
  }, [gameState, roomCode, triggerDealAnimation]);

  // Cleanup deal timer on unmount only
  useEffect(() => {
    const timeoutSet = timeoutsRef.current;
    return () => {
      if (dealTimerRef.current) clearInterval(dealTimerRef.current);
      if (soundLongPressRef.current) clearTimeout(soundLongPressRef.current);
      timeoutSet.forEach(clearTimeout);
      timeoutSet.clear();
    };
  }, []);

  // Show penalty card notifications from game state (authoritative, identical for all players)
  useEffect(() => {
    if (!dealing && !hasShownPenaltyRef.current && gameState?.penaltyMessages?.length) {
      hasShownPenaltyRef.current = true;
      const delay = 500;
      gameState.penaltyMessages.forEach((msg, i) => {
        const timer = scheduleTimeout(() => {
          const id = Date.now() + i;
          setNotifications((prev) => [...prev.slice(-4), { id, type: 'warning', message: msg }]);
          const clearTimer = scheduleTimeout(() => {
            setNotifications((prev) => prev.filter((n) => n.id !== id));
          }, 5000);
          notificationTimersRef.current.push(clearTimer);
        }, delay + i * 300);
        notificationTimersRef.current.push(timer);
      });
    }
  }, [dealing, gameState, scheduleTimeout]);

  // Show controls intro once after first deal completes
  useEffect(() => {
    if (!dealing && !hasShownIntroRef.current && !localStorage.getItem('ace_controls_seen')) {
      hasShownIntroRef.current = true;
      const timer = scheduleTimeout(() => setShowControlsIntro(true), 500);
      const timeoutSet = timeoutsRef.current;
      return () => {
        clearTimeout(timer);
        timeoutSet.delete(timer);
      };
    }
  }, [dealing, scheduleTimeout]);

  // Trigger fly transition one frame after cutFly is set
  useEffect(() => {
    if (!cutFly) {
      setFlyStarted(false);
      return;
    }
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => setFlyStarted(true));
    });
    return () => cancelAnimationFrame(raf);
  }, [cutFly]);

  // Turn timer countdown (suppress during dealing, skip if no limit)
  useEffect(() => {
    if (!gameState || gameState.gameOver || gameState.roundStopped || dealing || gameState.turnDuration <= 0) {
      setTimerSeconds(null);
      return;
    }

    const { turnStartTime, turnDuration } = gameState;
    const update = () => {
      const elapsed = (Date.now() - turnStartTime) / 1000;
      const remaining = Math.min(turnDuration, Math.max(0, turnDuration - elapsed));
      setTimerSeconds(Math.round(remaining));
    };

    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [gameState?.turnStartTime, gameState?.turnDuration, gameState?.gameOver, gameState?.roundStopped, dealing]); // eslint-disable-line react-hooks/exhaustive-deps

  // Event detection — sounds and animations
  useEffect(() => {
    if (!gameState) return;
    const prev = prevRef.current;
    const isMyTurn = gameState.currentPlayerIndex === gameState.myIndex && !gameState.gameOver;

    // New card played
    if (gameState.centerPile.length > prev.centerPileLen && prev.centerPileLen >= 0) {
      Sounds.cardPlay();
    }

    // CUT detected (round stopped + last card doesn't match lead suit)
    if (gameState.roundStopped && !prev.roundStopped) {
      const lastCard = gameState.centerPile[gameState.centerPile.length - 1];
      if (lastCard && gameState.leadSuit && lastCard.suit !== gameState.leadSuit) {
        setShowCut(true);
        Sounds.cut();
        Haptics.cut();
        scheduleTimeout(() => setShowCut(false), 2000);

        if (gameState.penaltyPlayerIndex != null) {
          const snapshot = gameState.centerPile.map((c) => ({
            id: c.id, suit: c.suit, rank: c.rank, playerIndex: c.playerIndex,
          }));
          const target = gameState.penaltyPlayerIndex;
          scheduleTimeout(() => {
            setCutFly({ targetIdx: target, cards: snapshot });
            scheduleTimeout(() => setCutFly(null), 1800);
          }, 1000);
        }
      }
    }

    // Your turn (suppress during dealing animation, brief delay after a card play)
    const turnJustBecameMine = isMyTurn && !prev.isMyTurn;
    const dealingJustEnded = prev.dealing && !dealing;
    const shouldShowTurn = (turnJustBecameMine || (dealingJustEnded && isMyTurn)) && !gameState.roundStopped && !dealing;

    if (shouldShowTurn) {
      const cardJustPlayed = gameState.centerPile.length > prev.centerPileLen && prev.centerPileLen >= 0;
      const delay = cardJustPlayed ? 800 : 0;
      scheduleTimeout(() => {
        setShowYourTurn(true);
        Sounds.yourTurn();
        Haptics.turn();
        scheduleTimeout(() => setShowYourTurn(false), 1500);
      }, delay);
    }

    // Borrow incoming
    if (gameState.borrowRequest && gameState.borrowRequest.targetIndex === gameState.myIndex) {
      Sounds.borrowIncoming();
    }

    // Player became safe
    const safeIndices = gameState.players.filter((p) => p.isSafe).map((p) => p.index);
    // Reuse the previous Set if safe players haven't changed — avoids allocation on every update
    const prevSafeSet = prev.safeSet;
    const safeCountChanged = safeIndices.length !== prevSafeSet.size;
    const currentSafe = safeCountChanged ? new Set(safeIndices) : prevSafeSet;
    if (!dealing && safeCountChanged && currentSafe.size > prevSafeSet.size) {
      const newlySafe = safeIndices.find((idx) => !prevSafeSet.has(idx));
      if (newlySafe !== undefined) {
        Sounds.playerSafe();
        setSafeAnimIdx(newlySafe);
        Haptics.safe();
        scheduleTimeout(() => setSafeAnimIdx(null), 2000);
      }
    }
    if (!dealing && !gameState.gameOver && !hasShownResignNudgeRef.current) {
      const activeCount = gameState.players.filter((p) => !p.isSafe).length;
      const me = gameState.players.find((p) => p.index === gameState.myIndex);
      if (activeCount === 2 && me && !me.isSafe) {
        hasShownResignNudgeRef.current = true;
        setShowResignNudge(true);
        scheduleTimeout(() => setShowResignNudge(false), 5000);
      }
    }

    // Game over
    if (gameState.gameOver && !prev.gameOver) {
      if (gameState.loserIndex === gameState.myIndex) {
        Sounds.gameOverLose();
        Haptics.lose();
      } else {
        Sounds.gameOverWin();
        Haptics.win();
      }
      const me = gameState.players.find((p) => p.index === gameState.myIndex);
      if (me && !me.isComputer) {
        recordLocalGameResult({
          isLoser: gameState.loserIndex === gameState.myIndex,
          isSafe: me.isSafe,
          cutsReceived: 0,
        });
      }
    }

    // New game started (rematch) — reset deal animation for all players
    if (prev.gameOver && !gameState.gameOver) {
      hasShownPenaltyRef.current = false;
      triggerDealAnimation(gameState);
    }

    prevRef.current = {
      roundStopped: gameState.roundStopped,
      isMyTurn,
      gameOver: gameState.gameOver,
      centerPileLen: gameState.centerPile.length,
      dealing,
      safeSet: currentSafe,
    };
  }, [gameState, dealing, roomCode, scheduleTimeout, triggerDealAnimation]);

  // Timer tick sounds (only for current player, under 10s)
  useEffect(() => {
    if (timerSeconds === null || timerSeconds <= 0 || timerSeconds > 10) return;
    if (!gameState || gameState.currentPlayerIndex !== gameState.myIndex) return;
    if (timerSeconds <= 5) {
      Sounds.timerUrgent();
    } else {
      Sounds.timerTick();
    }
  }, [timerSeconds, gameState]);

  const addNotification = useCallback((type: string, message: string) => {
    const id = Date.now();
    setNotifications((prev) => [...prev.slice(-4), { id, type, message }]);
    scheduleTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, 3000);
  }, [scheduleTimeout]);

  const playCard = useCallback(
    (cardId: string) => {
      if (!gameState) return;
      const { myIndex: mi, currentPlayerIndex: cpi, gameOver: go, roundStopped: rs, leadSuit: ls, myCards: mc } = gameState;

      if (go) return;
      if (isResyncing) {
        addNotification('warning', 'Game is reconnecting. Please wait for sync.');
        return;
      }
      if (dealing) {
        addNotification('warning', 'Wait for cards to be dealt');
        return;
      }
      if (rs) {
        addNotification('warning', 'Round is being resolved…');
        return;
      }
      if (cpi !== mi) {
        addNotification('warning', 'Not your turn');
        return;
      }
      const card = mc.find((c) => c.id === cardId);
      if (card && !canPlayCard(card, (ls as Suit) || null, mc)) {
        const suitName = ls ? ls.charAt(0).toUpperCase() + ls.slice(1) : '';
        const symbol = ls ? SUIT_SYMBOLS[ls] : '';
        addNotification('warning', `You must play a ${suitName} ${symbol}`);
        return;
      }
      setSelectedCardId(null);
      Haptics.tap();
      socket.emit('play_card', { cardId });
    },
    [gameState, addNotification, isResyncing, dealing]
  );

  const handleCardClick = useCallback(
    (cardId: string) => {
      if (confirmMode) {
        setSelectedCardId((prev) => (prev === cardId ? null : cardId));
      } else {
        playCard(cardId);
      }
    },
    [confirmMode, playCard]
  );

  const confirmSelectedCard = useCallback(() => {
    if (selectedCardId) playCard(selectedCardId);
  }, [selectedCardId, playCard]);

  const requestGameFullscreen = useCallback(async () => {
    if (document.fullscreenElement) return true;
    const target = document.documentElement;
    if (!target.requestFullscreen) return false;
    try {
      await target.requestFullscreen();
      return true;
    } catch {
      return false;
    }
  }, []);

  const tryLockLandscape = useCallback(async () => {
    const orientationApi = window.screen.orientation as ScreenOrientationApi | undefined;
    if (!orientationApi?.lock) {
      setOrientationLockFailed(false);
      return false;
    }
    try {
      await orientationApi.lock('landscape');
      setOrientationLockFailed(false);
      return true;
    } catch {
      setOrientationLockFailed(true);
      return false;
    }
  }, []);

  const enterLandscapeMode = useCallback(async () => {
    setHasAcknowledgedLandscapeGate(true);
    setIsEnteringLandscapeMode(true);
    await requestGameFullscreen();
    await tryLockLandscape();
    setIsEnteringLandscapeMode(false);
  }, [requestGameFullscreen, tryLockLandscape]);

  const toggleConfirmMode = useCallback(() => {
    setConfirmMode((prev) => {
      const next = !prev;
      localStorage.setItem('ace_confirm_mode', String(next));
      if (!next) setSelectedCardId(null);
      return next;
    });
  }, []);

  const sendBorrowRequest = (targetIndex: number) => {
    socket.emit('borrow_request', { targetPlayerIndex: targetIndex });
    setShowBorrowMenu(null);
  };

  const handleBorrowAccept = () => socket.emit('borrow_respond', { accept: true });
  const handleBorrowReject = () => socket.emit('borrow_respond', { accept: false });

  const [showResignConfirm, setShowResignConfirm] = useState(false);
  const [showResignNudge, setShowResignNudge] = useState(false);
  const handleResign = () => {
    socket.emit('resign');
    setShowResignConfirm(false);
  };

  const handleLeave = () => {
    if (!gameState?.gameOver) {
      setShowLeaveConfirm(true);
      return;
    }
    socket.emit('leave_room');
    onLeave();
  };

  const confirmLeave = () => {
    setShowLeaveConfirm(false);
    socket.emit('leave_room');
    onLeave();
  };

  const gs = gameState;
  const gsPlayers = useMemo(() => gs?.players ?? [], [gs?.players]);
  const gsTotalPlayers = gsPlayers.length;

  const myCards = useMemo(
    () => (gs ? sortCards(gs.myCards, sortMode) : []),
    [gs?.myCards, sortMode] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const turnOrderMap = useMemo(() => {
    const map = new Map<number, number>();
    if (gs && !gs.gameOver && !dealing) {
      let cursor = gs.currentPlayerIndex;
      let ordinal = 1;
      for (let i = 0; i < gsTotalPlayers; i++) {
        const p = gsPlayers[cursor];
        if (p && p.cardCount > 0 && !p.isSafe) {
          map.set(cursor, ordinal);
          ordinal++;
        }
        cursor = (cursor + 1) % gsTotalPlayers;
      }
    }
    return map;
  }, [gs?.currentPlayerIndex, gsTotalPlayers, gsPlayers, gs?.gameOver, dealing]); // eslint-disable-line react-hooks/exhaustive-deps

  const adjacentForBorrow = useMemo((): number[] => {
    const myIdx = gs?.myIndex;
    if (myIdx === null || myIdx === undefined) return [];
    const adj: number[] = [];
    for (let i = 1; i < gsTotalPlayers; i++) {
      const idx = (myIdx + i) % gsTotalPlayers;
      if (gsPlayers[idx]?.cardCount > 0 && !gsPlayers[idx]?.isSafe) {
        adj.push(idx);
        break;
      }
    }
    for (let i = 1; i < gsTotalPlayers; i++) {
      const idx = (myIdx - i + gsTotalPlayers) % gsTotalPlayers;
      if (gsPlayers[idx]?.cardCount > 0 && !gsPlayers[idx]?.isSafe) {
        if (!adj.includes(idx)) adj.push(idx);
        break;
      }
    }
    return adj;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gsTotalPlayers, gsPlayers, gs?.myIndex]);

  if (!gameState) {
    return (
      <div className="game-table">
        <div className="loading-message">Waiting for game data...</div>
      </div>
    );
  }

  const {
    myIndex,
    players,
    currentPlayerIndex,
    centerPile,
    leadSuit,
    roundStopped,
    gameOver,
    loserIndex,
    borrowRequest,
  } = gameState;

  const isMyTurn = currentPlayerIndex === myIndex && !gameOver;
  const totalPlayers = players.length;

  const borrowAllowed =
    !gameOver &&
    centerPile.length === 0 &&
    !roundStopped &&
    !borrowRequest;

  const isBorrowTarget =
    borrowRequest && borrowRequest.targetIndex === myIndex;
  const shouldShowLandscapeGate =
    isMobile &&
    !gameOver &&
    !hasAcknowledgedLandscapeGate &&
    !isLandscape;
  const shouldShowRotateHint = showRotateHint && !shouldShowLandscapeGate;

  return (
    <div className={`game-table ${showCut ? 'shake' : ''} ${isLandscape ? 'landscape' : ''}`}>
      <div className="table-surface" ref={tableRef}>
        {/* Lead suit indicator */}
        {leadSuit && (
          <div className="lead-suit-badge">
            <span>Lead</span>
            <span className={`suit-icon ${isRedSuit(leadSuit) ? 'red' : 'black'}`}>
              {SUIT_SYMBOLS[leadSuit]}
            </span>
          </div>
        )}

        {/* Center pile */}
        {!cutFly && (
        <div className="center-pile">
          <AnimatePresence>
              {centerPile.map((card, idx) => (
                <div
                  key={`${card.id}-${idx}`}
                  className="center-card-wrapper"
                  style={{
                    zIndex: idx,
                    ...getCenterCardPosition(card.playerIndex, idx, totalPlayers, myIndex),
                  }}
                >
                <motion.div
                  initial={{ scale: 0, rotate: 180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  exit={{ scale: 0, rotate: -180 }}
                    transition={{ duration: 0.4, delay: idx * 0.08 }}
                  >
                    <Card card={{ ...card, isFaceUp: true }} />
                  </motion.div>
                </div>
              ))}
            </AnimatePresence>
          </div>
        )}

        {/* Cut penalty — cards fly to penalty player (positioned relative to table-surface) */}
        {cutFly && (() => {
          const seatPos = getPlayerSeatPosition(cutFly.targetIdx, totalPlayers, isMobile, myIndex, isLandscape);
          const targetLeft = parseFloat(seatPos.left as string);
          const targetTop = parseFloat(seatPos.top as string);

          // Convert center-pile-relative % to table-surface-relative %
          const tableW = tableRef.current?.offsetWidth || (isLandscape ? window.innerWidth - 180 : isMobile ? window.innerWidth : 1100);
          const tableH = tableRef.current?.offsetHeight || (isMobile ? window.innerHeight : 760);
          const cpW = isLandscape ? 160 : isMobile ? 160 : 200;
          const cpH = isLandscape ? 100 : isMobile ? 130 : 160;
          const cpCenterY = isMobile && !isLandscape ? 45 : 50;

          return cutFly.cards.map((card, idx) => {
            const srcPos = getCenterCardPosition(card.playerIndex, idx, totalPlayers, myIndex);
            const cpLeft = parseFloat(srcPos.left as string);
            const cpTop = parseFloat(srcPos.top as string);
            // Map center-pile % → table-surface %
            const srcLeft = 50 + (cpLeft - 50) * cpW / tableW;
            const srcTop = cpCenterY + (cpTop - 50) * cpH / tableH;
            const arrived = flyStarted;
            return (
              <div
                key={`fly-${card.id}`}
                className="cut-fly-card"
                  style={{
                    position: 'absolute',
                  zIndex: 60 + idx,
                  left: arrived ? `${targetLeft}%` : `${srcLeft}%`,
                  top: arrived ? `${targetTop}%` : `${srcTop}%`,
                  opacity: arrived ? 0 : 1,
                  transform: `translate(-50%, -50%) scale(${arrived ? 0.6 : 1})`,
                  transition: `left 1s ease-in-out ${idx * 0.08}s, top 1s ease-in-out ${idx * 0.08}s, transform 1s ease-in-out ${idx * 0.08}s, opacity 0.1s ease-in ${0.95 + idx * 0.08}s`,
                  pointerEvents: 'none' as const,
                }}
              >
                <Card
                  card={{
                    suit: card.suit as Suit,
                    rank: card.rank as Rank,
                    id: card.id,
                    isFaceUp: true,
                  }}
                />
              </div>
            );
          });
        })()}

        {/* Dealing animation */}
        <AnimatePresence>
          {dealing && (
            <motion.div
              className="deal-container"
              initial={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              {/* Central deck — shrinks as cards are dealt */}
              <div className="deal-deck">
                {Array.from({ length: Math.min(8, Math.max(0, dealSeqRef.current.length - dealtCount)) }).map((_, i) => (
                  <div
                    key={`deck-${i}`}
                    className="deal-deck-card"
                    style={{ transform: `translate(-50%, -50%) rotate(${i * 2 - 4}deg) translateY(${-i}px)` }}
                  />
                ))}
              </div>
              <div className="dealing-label">Dealing…</div>

              {/* Cards flying to each player one at a time */}
              {dealSeqRef.current.slice(Math.max(0, dealtCount - 6), dealtCount).map((pIdx, visibleIdx) => {
                const i = Math.max(0, dealtCount - 6) + visibleIdx;
                const pos = getPlayerSeatPosition(pIdx, totalPlayers, isMobile, myIndex, isLandscape);
                return (
                  <div
                    key={`deal-${i}`}
                    className="deal-card-fly"
                    style={{
                      '--tx': pos.left,
                      '--ty': pos.top,
                      '--rot': `${(i * 7 + pIdx * 13) % 20 - 10}deg`,
                    } as React.CSSProperties}
                  />
              );
            })}
            </motion.div>
          )}
          </AnimatePresence>

        {/* Player seats */}
        {players.map((player: ClientPlayer) => {
          const idx = player.index;
          const isTurn = player.isCurrentPlayer && !gameOver && !dealing;
          const isMe = idx === myIndex;

          return (
            <div
              key={idx}
              className={`player-seat ${isTurn ? 'is-current' : ''} ${player.isSafe ? 'is-safe' : ''} ${gameOver && loserIndex === idx ? 'is-loser' : ''} ${isMe ? 'is-me' : ''}`}
              style={getPlayerSeatPosition(idx, totalPlayers, isMobile, myIndex, isLandscape)}
            >
              {/* Badge */}
              <div className="player-badge">
                {isTurn && <span className="turn-dot" />}
                <div className="avatar-wrapper">
                  <span
                    className="badge-avatar"
                    style={{ background: PLAYER_AVATARS[idx % PLAYER_AVATARS.length].bg }}
                  >
                    {player.isComputer
                      ? '🤖'
                      : player.isDisconnected
                      ? '⚡'
                      : PLAYER_AVATARS[idx % PLAYER_AVATARS.length].emoji}
                  </span>
                  {isTurn && timerSeconds !== null && (
                    <svg className="timer-ring" viewBox="0 0 36 36">
                      <circle
                        className="timer-ring-bg"
                        cx="18" cy="18" r="16"
                      />
                      <circle
                        className={`timer-ring-progress ${timerSeconds <= 10 ? 'urgent' : ''}`}
                        cx="18" cy="18" r="16"
                        strokeDasharray="100.53"
                        strokeDashoffset={100.53 * (1 - timerSeconds / (gameState?.turnDuration ?? 30))}
                      />
                    </svg>
                  )}
                </div>
                <span className="badge-name">
                  {player.name}
                  {isMe && <span className="me-tag"> (You)</span>}
                </span>
                <span className="badge-count">{player.cardCount}</span>
                {turnOrderMap.get(idx) === 1 && (
                  <span className="turn-order now">NOW</span>
                )}
                {turnOrderMap.get(idx) === 2 && (
                  <span className="turn-order next">NEXT</span>
                )}
                {player.isSafe && <span className="badge-status safe">Safe</span>}
                {gameOver && loserIndex === idx && (
                  <span className="badge-status loser">Loser</span>
                )}
                {player.isDisconnected && (
                  <span className="badge-status disconnected">Offline</span>
                )}
                {borrowRequest && borrowRequest.requesterIndex === idx && (
                  <span className="badge-status borrowing">Borrowing…</span>
                )}
                {borrowRequest && borrowRequest.targetIndex === idx && !isMe && (
                  <span className="badge-status borrow-target">Requested</span>
                )}
                {borrowAllowed && isMe && player.cardCount > 0 && !player.isSafe && (
                  <button
                    className="borrow-trigger"
                    onClick={() => setShowBorrowMenu(showBorrowMenu === idx ? null : idx)}
                    title="Borrow cards from neighbor"
                  >
                    🤝
                  </button>
                )}
                {isMe && !player.isSafe && !gameOver &&
                  players.filter((p) => !p.isSafe).length === 2 && (
                  <button
                    className={`resign-trigger${showResignNudge ? ' resign-highlight' : ''}`}
                    onClick={() => { setShowResignConfirm(true); setShowResignNudge(false); }}
                    title="Resign (accept loss)"
                  >
                    🚩
                  </button>
                )}
              </div>

              {/* Safe animation burst */}
              <AnimatePresence>
                {safeAnimIdx === idx && (
                  <motion.div
                    className="safe-burst"
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 1.5, opacity: 0 }}
                    transition={{ duration: 0.4, ease: 'easeOut' }}
                  >
                    SAFE!
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Borrow menu is now rendered as a fixed overlay outside the player seat */}

              {/* My hand — face-up, playable */}
              {isMe && myCards.length > 0 && !dealing && (
                <div className="player-hand my-hand">
                  {myCards.map((card, ci) => {
                    const canPlay = canPlayCard(card, (leadSuit as Suit) || null, myCards);
                    const playable = isMyTurn && !roundStopped && canPlay;
                    const blurred = !canPlay && !!leadSuit;
                    const selected = confirmMode && selectedCardId === card.id;

                    return (
                      <motion.div
                        key={card.id}
                        className={`hand-card ${selected ? 'hand-card-selected' : ''}`}
                        style={{ zIndex: ci }}
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: selected ? -12 : 0 }}
                        transition={{
                          duration: 0.35,
                          delay: ci * 0.03,
                          type: 'spring',
                          stiffness: 120,
                        }}
                      >
                        <Card
                          card={{ ...card, isFaceUp: true }}
                          onClick={() => handleCardClick(card.id)}
                          isPlayable={playable}
                          className={`${blurred ? 'blurred' : ''} ${roundStopped ? 'round-stopped' : ''}`}
                        />
                      </motion.div>
                    );
                  })}
                </div>
              )}

              {/* Other players' hand — face-down */}
              {!isMe && player.cardCount > 0 && !player.isSafe && !dealing && (
                <div className="player-hand other-hand">
                  {Array.from({ length: Math.min(player.cardCount, 10) }).map((_, ci) => (
                    <div key={ci} className="hand-card" style={{ zIndex: ci }}>
                      <Card
                        card={{ id: `back-${idx}-${ci}`, suit: Suit.SPADES, rank: Rank.ACE, isFaceUp: false }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* Start button placeholder (not needed for multiplayer - game starts from lobby) */}
      </div>

      {/* Borrow neighbor selector — fixed overlay */}
      {showBorrowMenu !== null && myIndex !== null && showBorrowMenu === myIndex && (
        <>
          <div className="borrow-menu-backdrop" onClick={() => setShowBorrowMenu(null)} />
          <div className="borrow-menu" onClick={(e) => e.stopPropagation()}>
            <span className="borrow-menu-title">Borrow from…</span>
            {adjacentForBorrow.map((nIdx) => (
              <button
                key={nIdx}
                className="borrow-menu-option"
                onClick={(e) => { e.stopPropagation(); sendBorrowRequest(nIdx); }}
              >
                <span
                  className="badge-avatar small"
                  style={{ background: PLAYER_AVATARS[nIdx % PLAYER_AVATARS.length].bg }}
                >
                  {players[nIdx].isComputer
                    ? '🤖'
                    : PLAYER_AVATARS[nIdx % PLAYER_AVATARS.length].emoji}
                </span>
                {players[nIdx].name} ({players[nIdx].cardCount})
              </button>
            ))}
            <button
              className="borrow-menu-cancel"
              onClick={(e) => { e.stopPropagation(); setShowBorrowMenu(null); }}
            >
              Cancel
            </button>
          </div>
        </>
      )}

      {/* Mobile tray: my hand */}
      {!gameOver && myCards.length > 0 && !dealing && (
        <div className="mobile-tray">
          <div className="tray-header">
            <div className="tray-status">
              <span className={`tray-turn-badge ${isMyTurn ? 'my-turn' : ''}`}>
                {isMyTurn ? '▶ YOUR TURN' : `${players[currentPlayerIndex]?.name}'s turn`}
              </span>
              {/* Always reserve space for the suit badge — hidden when no lead suit to prevent layout shift */}
              <span
                className="tray-suit-badge"
                style={leadSuit ? undefined : { visibility: 'hidden' }}
                aria-hidden={!leadSuit}
              >
                <span className={leadSuit && isRedSuit(leadSuit) ? 'red' : ''}>
                  {leadSuit ? SUIT_SYMBOLS[leadSuit] : SUIT_SYMBOLS['spades']}
                </span>
              </span>
            </div>
            <div className="tray-actions">
              <span className={`tray-timer-badge ${timerSeconds !== null && timerSeconds <= 10 ? 'urgent' : ''}`}>
                {timerSeconds !== null ? `${timerSeconds}s` : '—'}
              </span>
              {(() => {
                const canBorrow = borrowAllowed && myIndex !== null && !players[myIndex]?.isSafe && (players[myIndex]?.cardCount ?? 0) > 0;
                const activeCount = players.filter((p) => !p.isSafe).length;
                const canResign = activeCount === 2 && !gameOver && myIndex !== null && !players[myIndex]?.isSafe;
                return (
                  <>
                    <button
                      className={`tray-borrow-btn ${!canBorrow ? 'disabled' : ''}`}
                      disabled={!canBorrow}
                      onClick={() => canBorrow && setShowBorrowMenu(showBorrowMenu === myIndex ? null : myIndex!)}
                    >
                      <span className="tray-action-icon">🤝</span>
                      <span className="tray-action-label">Borrow</span>
                    </button>
                    {canResign && (
                      <button
                        className={`tray-resign-btn${showResignNudge ? ' resign-highlight' : ''}`}
                        onClick={() => { setShowResignConfirm(true); setShowResignNudge(false); }}
                        title="Resign (accept loss)"
                      >
                        <span className="tray-action-icon">🚩</span>
                        <span className="tray-action-label">Resign</span>
                      </button>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
          <div className="tray-cards">
            {myCards.map((card) => {
              const canPlay = canPlayCard(card, (leadSuit as Suit) || null, myCards);
              const playable = isMyTurn && !roundStopped && canPlay;
              const blurred = !canPlay && !!leadSuit;
              const selected = confirmMode && selectedCardId === card.id;

              return (
                <div key={card.id} className={`tray-card-wrapper ${selected ? 'tray-card-selected' : ''}`}>
                  <Card
                    card={{ ...card, isFaceUp: true }}
                    onClick={() => handleCardClick(card.id)}
                    isPlayable={playable}
                    className={`${blurred ? 'blurred' : ''} ${roundStopped ? 'round-stopped' : ''}`}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Exit button — top right */}
      {!gameOver && (
        <button
          className="game-exit-btn"
          onClick={handleLeave}
          title="Leave game"
        >
          Exit
        </button>
      )}

      {/* HUD — desktop only; CSS hides it on all touch/mobile devices */}
      {!gameOver && (
        <div className="game-hud">
          <div className="hud-row">
            <span className="hud-label">Room:</span>
            <span className="hud-value room-code">{roomCode}</span>
          </div>
          <div className="hud-row">
            <span className="hud-label">Turn:</span>
            <span className="hud-value">
              {isMyTurn ? 'You' : players[currentPlayerIndex]?.name}
            </span>
          </div>
          <div className="hud-row">
            <span className="hud-label">Timer:</span>
            <span className={`hud-value timer ${timerSeconds !== null && timerSeconds <= 10 ? 'timer-urgent' : ''}`}>
              {timerSeconds !== null ? `${timerSeconds}s` : '—'}
            </span>
          </div>
          <div className="hud-row">
            <span className="hud-label">Pile:</span>
            <span className="hud-value">{centerPile.length} cards</span>
          </div>
          <div className="hud-row">
            <span className="hud-label">Lead:</span>
            <span className={`hud-suit ${leadSuit ? (isRedSuit(leadSuit) ? 'red' : 'black') : ''}`}>
              {leadSuit ? SUIT_SYMBOLS[leadSuit] : '—'}
            </span>
          </div>
          <div className="hud-row">
            <span className="hud-label">Status:</span>
            <span className={`hud-value ${roundStopped ? 'stopped' : 'active'}`}>
              {roundStopped ? 'Cut!' : 'Active'}
            </span>
          </div>
        </div>
      )}

      {/* Borrow confirmation dialog (for borrow target) */}
      {isBorrowTarget && borrowRequest && (
        <motion.div
          className="borrow-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.25 }}
        >
          <div className="borrow-dialog">
            <div className="borrow-dialog-header">
              <span
                className="badge-avatar"
                style={{
                  background:
                    PLAYER_AVATARS[borrowRequest.requesterIndex % PLAYER_AVATARS.length].bg,
                }}
              >
                {PLAYER_AVATARS[borrowRequest.requesterIndex % PLAYER_AVATARS.length].emoji}
              </span>
              <span className="borrow-dialog-title">Borrow Request</span>
            </div>
            <p className="borrow-dialog-text">
              <strong>{players[borrowRequest.requesterIndex]?.name}</strong> wants to take
              all <strong>{myCards.length}</strong> of your cards.
            </p>
            <p className="borrow-dialog-subtext">
              If you accept, you'll be Safe and leave the game.
            </p>
            <div className="borrow-dialog-actions">
              <button className="borrow-accept" onClick={handleBorrowAccept}>
                Accept
              </button>
              <button className="borrow-reject" onClick={handleBorrowReject}>
                Reject
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {/* CUT! Overlay */}
      <AnimatePresence>
        {showCut && (
          <motion.div
            className="cut-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <motion.span
              className="cut-text"
              initial={{ scale: 0, rotate: -15 }}
              animate={{ scale: 1, rotate: 0 }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 15 }}
            >
              CUT!
            </motion.span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* YOUR TURN Banner */}
      <AnimatePresence>
        {showYourTurn && !isMobile && (
          <motion.div
            className="your-turn-banner"
            initial={{ y: -80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -80, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 200, damping: 20 }}
          >
            YOUR TURN
          </motion.div>
        )}
      </AnimatePresence>

      {/* Top Controls: Help, Sound, Sort */}
      <div className="game-controls-top">
        <button
          className="game-control-btn"
          onClick={() => setShowRules(true)}
          title="How to Play"
        >
          ?
        </button>
        <div className="sound-control-wrapper">
          <button
            className={`game-control-btn ${showVolumeSlider ? 'active' : ''}`}
            onClick={() => {
              if (soundTapFiredRef.current) { soundTapFiredRef.current = false; return; }
              const next = !soundOn;
              setSoundOn(next);
              setSoundEnabled(next);
            }}
            onContextMenu={(e) => {
              e.preventDefault();
              setShowVolumeSlider((v) => !v);
            }}
            onTouchStart={() => {
              soundTapFiredRef.current = false;
              soundLongPressRef.current = setTimeout(() => {
                soundTapFiredRef.current = true;
                setShowVolumeSlider((v) => !v);
              }, 400);
            }}
            onTouchEnd={() => {
              if (soundLongPressRef.current) { clearTimeout(soundLongPressRef.current); soundLongPressRef.current = null; }
            }}
            onTouchCancel={() => {
              if (soundLongPressRef.current) { clearTimeout(soundLongPressRef.current); soundLongPressRef.current = null; }
            }}
            title={soundOn ? 'Tap to mute · Long-press for volume' : 'Tap to unmute · Long-press for volume'}
          >
            {soundOn ? (volume > 0.5 ? '🔊' : volume > 0 ? '🔉' : '🔇') : '🔇'}
          </button>
          <AnimatePresence>
            {showVolumeSlider && (
              <motion.div
                className="volume-slider-popup"
                initial={{ opacity: 0, y: -8, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -8, scale: 0.95 }}
                transition={{ duration: 0.15 }}
              >
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={volume}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value);
                    setVolumeState(v);
                    setVolume(v);
                    if (v > 0 && !soundOn) {
                      setSoundOn(true);
                      setSoundEnabled(true);
                    } else if (v === 0 && soundOn) {
                      setSoundOn(false);
                      setSoundEnabled(false);
                    }
                  }}
                  className="volume-slider"
                />
                <span className="volume-value">{Math.round(volume * 100)}%</span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <button
          className="game-control-btn sort-toggle"
          onClick={() => {
            const nextIdx = (SORT_CYCLE.indexOf(sortMode) + 1) % SORT_CYCLE.length;
            setSortMode(SORT_CYCLE[nextIdx]);
          }}
          title={`Sort: ${SORT_LABELS[sortMode]}`}
        >
          <span className="sort-icon">⇅</span>
          <span className="sort-label">{SORT_LABELS[sortMode]}</span>
        </button>
        <button
          className={`game-control-btn confirm-toggle ${confirmMode ? 'active' : ''}`}
          onClick={toggleConfirmMode}
          title={confirmMode ? 'Confirm mode ON — tap card then confirm' : 'Confirm mode OFF — tap to play instantly'}
        >
          <span className="confirm-icon">✓</span>
          <span className="confirm-label">{confirmMode ? 'On' : 'Off'}</span>
        </button>
        <button
          className="game-control-btn"
          onClick={() => setShowControlsIntro(true)}
          title="What are these buttons?"
        >
          ℹ
        </button>
        <button
          className={`game-control-btn fullscreen-btn ${isFullscreen ? 'active' : ''}`}
          onClick={() => {
            if (document.fullscreenElement) {
              document.exitFullscreen().catch(() => {});
              return;
            }
            if (isMobile) {
              void enterLandscapeMode();
              return;
            }
            document.documentElement.requestFullscreen().catch(() => {});
          }}
          title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
        >
          ⛶
        </button>
      </div>


      <RulesModal open={showRules} onClose={() => setShowRules(false)} />

      {/* Card confirm bar */}
      <AnimatePresence>
        {confirmMode && selectedCardId && (() => {
          const card = gameState.myCards.find((c) => c.id === selectedCardId);
          if (!card) return null;
          const symbol = SUIT_SYMBOLS[card.suit] || '';
          const suitColor = isRedSuit(card.suit) ? 'red' : 'black';
          return (
            <motion.div
              className="confirm-bar"
              initial={{ y: 60, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 60, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            >
              <span className="confirm-bar-card">
                <span className={`confirm-suit ${suitColor}`}>{symbol}</span>
                {card.rank}
              </span>
              <button className="confirm-bar-play" onClick={confirmSelectedCard}>
                Play
              </button>
              <button className="confirm-bar-cancel" onClick={() => setSelectedCardId(null)}>
                Cancel
              </button>
            </motion.div>
          );
        })()}
      </AnimatePresence>

      {/* Rotate hint (mobile portrait, one-time) */}
      <AnimatePresence>
        {shouldShowRotateHint && (
          <motion.div
            className="rotate-hint"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.4 }}
            onClick={() => setShowRotateHint(false)}
          >
            <span className="rotate-hint-icon">📱↻</span>
            <span>Rotate for best experience</span>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isResyncing && (
          <motion.div
            className="game-resync-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="game-resync-card">
              <div className="game-resync-title">Reconnecting Game…</div>
              <div className="game-resync-text">
                Restoring your seat and fetching the latest turn state.
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {shouldShowLandscapeGate && (
          <motion.div
            className="landscape-gate-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <motion.div
              className="landscape-gate-card"
              role="dialog"
              aria-modal="true"
              aria-label="Rotate your phone to play"
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', stiffness: 320, damping: 32 }}
            >
              <div className="landscape-gate-icon">📱</div>
              <h3>Rotate your phone</h3>
              <p>
                The game plays best in landscape. Turn your phone sideways for the full table view.
              </p>
              <button
                className="landscape-gate-action"
                onClick={() => {
                  void enterLandscapeMode();
                }}
                disabled={isEnteringLandscapeMode}
              >
                {isEnteringLandscapeMode ? 'Rotating…' : 'Rotate & Play'}
              </button>
              <button
                className="landscape-gate-skip"
                onClick={() => setHasAcknowledgedLandscapeGate(true)}
              >
                Continue in portrait
              </button>
              {orientationLockFailed && (
                <p className="landscape-gate-lock-note">
                  Auto-rotate blocked — turn your phone sideways manually.
                </p>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Notifications */}
      <div className="notification-stack" role="status" aria-live="polite" aria-atomic="false">
        <AnimatePresence>
          {notifications.map((n) => (
            <motion.div
              key={n.id}
              className={`notification-toast ${n.type}`}
              initial={{ opacity: 0, y: -20, x: '-50%' }}
              animate={{ opacity: 1, y: 0, x: '-50%' }}
              exit={{ opacity: 0, y: -20, x: '-50%' }}
              transition={{ duration: 0.3 }}
            >
              {n.message}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Game Over */}
      {gameOver && loserIndex !== null && (() => {
        const readySet = new Set(gameState.readyPlayerIndices || []);
        const imReady = readySet.has(myIndex);
        const activeHumans = players.filter(
          (p) => !p.isComputer && !p.isDisconnected
        );
        const readyCount = activeHumans.filter((p) => readySet.has(p.index)).length;
        const totalHumans = activeHumans.length;

        return (
          <motion.div
            className="game-over-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="game-over-content">
              <h2>Game Over</h2>
              <p>
                {loserIndex === myIndex
                  ? 'You are the Loser!'
                  : `${players[loserIndex]?.name} is the Loser!`}
              </p>

              {/* Ready status indicators */}
              <div className="ready-status" aria-live="polite" aria-label="Player readiness status">
                {activeHumans.map((p) => {
                  const isReady = readySet.has(p.index);
                  return (
                    <div key={p.index} className={`ready-player ${isReady ? 'is-ready' : ''}`}>
                      <span className="ready-indicator">{isReady ? '✓' : '·'}</span>
                      <span className="ready-name">{p.index === myIndex ? 'You' : p.name}</span>
                    </div>
                  );
                })}
              </div>

              {readyCount > 0 && readyCount < totalHumans && (
                <p className="ready-waiting">
                  {readyCount}/{totalHumans} ready
                </p>
              )}

              <div className="game-over-actions">
                {!imReady ? (
                  <motion.button
                    className="start-button ready-btn"
                    onClick={() => socket.emit('rematch_ready')}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Ready
                  </motion.button>
                ) : (
                  <motion.div
                    className="ready-confirmed"
                    initial={{ scale: 0.9 }}
                    animate={{ scale: 1 }}
                  >
                    ✓ Waiting for others…
                  </motion.div>
                )}
                <motion.button
                  className="stats-button"
                  onClick={() => setShowProfile(true)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  View Stats
                </motion.button>
                <motion.button
                  className="leave-button"
                  onClick={handleLeave}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Back to Home
                </motion.button>
              </div>
            </div>
          </motion.div>
        );
      })()}

      <Profile open={showProfile} onClose={() => setShowProfile(false)} />

      <ControlsIntro
        open={showControlsIntro}
        onClose={() => {
          setShowControlsIntro(false);
          localStorage.setItem('ace_controls_seen', '1');
        }}
      />

      <ConfirmDialog
        open={showLeaveConfirm}
        title="Leave Game?"
        message="Your spot will be taken over by a computer player. Are you sure you want to leave?"
        confirmText="Leave"
        cancelText="Stay"
        variant="danger"
        onConfirm={confirmLeave}
        onCancel={() => setShowLeaveConfirm(false)}
      />

      <ConfirmDialog
        open={showResignConfirm}
        title="Resign?"
        message="You will accept the loss and the game will end. Are you sure?"
        confirmText="Resign"
        cancelText="Keep Playing"
        variant="danger"
        onConfirm={handleResign}
        onCancel={() => setShowResignConfirm(false)}
      />

      <AnimatePresence>
        {showResignNudge && (
          <motion.div
            className="resign-nudge-overlay"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            onClick={() => setShowResignNudge(false)}
          >
            <span className="resign-nudge-icon">🚩</span>
            <span>You can now resign</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default GameTable;
